import { useState, useEffect, useRef } from 'react'
import { Link } from 'react-router-dom'
import { motion, useScroll, useTransform, useInView, AnimatePresence } from 'framer-motion'
import {
  Calendar, Map, Users, MessageSquare, FileText, BarChart3,
  Shield, Lock, Cloud, Bell, ArrowRight, Check, Menu, X,
  Smartphone, Heart, Zap, Star, ChevronDown, Activity,
  ClipboardList, Route, ShieldCheck, Database, Wifi,
} from 'lucide-react'
import logo from '../assets/medisync_logo.png'

// ── Data ─────────────────────────────────────────────────────────────────────

const NAV_LINKS = [
  { href: '#fonctionnalites', label: 'Fonctionnalités' },
  { href: '#comment', label: 'Comment ça marche' },
  { href: '#mobile', label: 'Mobile' },
  { href: '#securite', label: 'Sécurité' },
  { href: '#faq', label: 'FAQ' },
]

const STATS = [
  { value: '2 400+', label: 'Patients suivis', icon: Users },
  { value: '98%', label: 'Satisfaction', icon: Star },
  { value: '50k+', label: 'Soins / mois', icon: Activity },
  { value: '99.9%', label: 'Disponibilité', icon: Zap },
]

const FEATURES = [
  {
    icon: Calendar,
    title: 'Planning intelligent',
    desc: 'Visualisez votre agenda de soins en temps réel. Rendez-vous, rappels automatiques et vue hebdomadaire intégrée.',
    color: '#3B82F6',
    gradient: 'from-blue-500/10 to-blue-600/5',
  },
  {
    icon: Route,
    title: 'Optimisation des tournées',
    desc: 'Préparez vos visites à partir des distances géographiques. La prise en compte des créneaux et priorités médicales est prévue dans une prochaine version.',
    color: '#10B981',
    gradient: 'from-emerald-500/10 to-emerald-600/5',
  },
  {
    icon: ClipboardList,
    title: 'Dossiers patients',
    desc: 'Accédez instantanément aux informations médicales, diagnostics, traitements et historiques de vos patients.',
    color: '#F59E0B',
    gradient: 'from-amber-500/10 to-amber-600/5',
  },
  {
    icon: MessageSquare,
    title: 'Messagerie sécurisée',
    desc: 'Échangez en temps réel avec vos collègues. Communications chiffrées et conformes aux normes de santé.',
    color: '#8B5CF6',
    gradient: 'from-violet-500/10 to-violet-600/5',
  },
  {
    icon: FileText,
    title: 'Rapports & PDF',
    desc: 'Générez vos comptes-rendus de soins en un clic. Exportez en PDF et archivez automatiquement.',
    color: '#EF4444',
    gradient: 'from-red-500/10 to-red-600/5',
  },
  {
    icon: BarChart3,
    title: 'Tableau de bord',
    desc: 'Suivez votre activité, charge de travail et indicateurs clés avec des graphiques temps réel.',
    color: '#06B6D4',
    gradient: 'from-cyan-500/10 to-cyan-600/5',
  },
]

const STEPS = [
  {
    step: '01',
    title: 'Créez votre compte',
    desc: 'Inscription en 2 minutes avec votre numéro RPPS. Connexion sécurisée via Pro Santé Connect.',
    icon: Users,
  },
  {
    step: '02',
    title: 'Importez vos patients',
    desc: 'Ajoutez vos patients avec leurs dossiers médicaux. Identification nationale via INS qualifiée.',
    icon: ClipboardList,
  },
  {
    step: '03',
    title: 'Planifiez vos tournées',
    desc: "Organisez votre agenda et vos visites à domicile. L'app mobile vous guide en temps réel.",
    icon: Map,
  },
  {
    step: '04',
    title: 'Gérez et communiquez',
    desc: 'Rédigez vos comptes-rendus, échangez avec vos collègues et suivez vos statistiques.',
    icon: Activity,
  },
]

const SECURITY_ITEMS = [
  { icon: Lock, title: 'Chiffrement AES-256', desc: 'Toutes les données de santé sont chiffrées au repos et en transit.' },
  { icon: ShieldCheck, title: 'Pro Santé Connect', desc: 'Authentification forte via carte CPS ou e-CPS, conforme ANS.' },
  { icon: Database, title: 'Hébergement HDS', desc: 'Données hébergées chez un hébergeur certifié HDS en France.' },
  { icon: Shield, title: 'Conformité RGPD', desc: 'Registre des traitements, AIPD et durées de conservation respectées.' },
  { icon: Wifi, title: 'TLS 1.3', desc: 'Communications chiffrées bout en bout sur tous les endpoints.' },
  { icon: Cloud, title: 'Sauvegardes chiffrées', desc: 'Sauvegardes quotidiennes automatiques avec rétention 30 jours.' },
]

const COMPLIANCE_BADGES = [
  { label: 'RGPD', sublabel: 'Conforme' },
  { label: 'HDS', sublabel: 'Certifié' },
  { label: 'INS', sublabel: 'Implémenté' },
  { label: 'PSC', sublabel: 'Intégré' },
]

const MOBILE_FEATURES = [
  'Synchronisation temps réel avec la version web',
  'Mode hors-ligne pour les zones sans réseau',
  'Navigation GPS intégrée pour les tournées',
  'Notifications push pour les rappels de soins',
  'Scanner de documents depuis l\'appareil photo',
  'Stockage sécurisé des tokens (iOS Keychain / Android Keystore)',
]

const FAQ = [
  {
    q: 'MediSync est-il conforme aux normes françaises de santé ?',
    a: 'Oui. MediSync implémente l\'INS (Identité Nationale de Santé), l\'authentification via Pro Santé Connect, et utilise un hébergement certifié HDS. La conformité RGPD est assurée avec un registre des traitements et une AIPD réalisée.',
  },
  {
    q: 'Mes données sont-elles stockées en France ?',
    a: 'Oui. Toutes les données de santé sont hébergées en France chez un prestataire certifié HDS (Hébergeur de Données de Santé), conformément à l\'article L.1111-8 du Code de la Santé Publique.',
  },
  {
    q: 'L\'application fonctionne-t-elle sans connexion internet ?',
    a: 'L\'application mobile dispose d\'un mode hors-ligne partiel. Les données en cache restent accessibles pour la consultation, et les modifications sont synchronisées dès la reconnexion.',
  },
  {
    q: 'Comment fonctionne l\'authentification Pro Santé Connect ?',
    a: 'MediSync intègre le protocole OpenID Connect (OIDC) de Pro Santé Connect. Les infirmiers peuvent se connecter avec leur carte CPS physique ou leur e-CPS (appli mobile ANS), sans mot de passe supplémentaire.',
  },
  {
    q: 'Quel est le coût de MediSync ?',
    a: 'MediSync est actuellement en phase de lancement. Contactez-nous pour connaître les modalités tarifaires adaptées à votre exercice libéral.',
  },
]

// ── Composants utilitaires ────────────────────────────────────────────────────

function FadeIn({ children, delay = 0, className = '' }: { children: React.ReactNode; delay?: number; className?: string }) {
  const ref = useRef(null)
  const inView = useInView(ref, { once: true, margin: '-80px' })
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 24 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, delay, ease: [0.22, 1, 0.36, 1] }}
      className={className}
    >
      {children}
    </motion.div>
  )
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <span className='inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.15em] text-primary-600 bg-primary-50 border border-primary-100 px-3 py-1.5 rounded-full'>
      {children}
    </span>
  )
}

// ── Page principale ───────────────────────────────────────────────────────────

export default function Landing() {
  const [menuOpen, setMenuOpen] = useState(false)
  const [scrolled, setScrolled] = useState(false)
  const [openFaq, setOpenFaq] = useState<number | null>(null)
  const { scrollY } = useScroll()
  const heroY = useTransform(scrollY, [0, 500], [0, -100])

  useEffect(() => {
    const unsub = scrollY.on('change', v => setScrolled(v > 40))
    return unsub
  }, [scrollY])

  return (
    <div className='min-h-screen bg-white text-gray-900 overflow-x-hidden font-sans'>

      {/* ── Navbar ─────────────────────────────────────────────────────────── */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? 'bg-white/90 backdrop-blur-xl shadow-[0_1px_0_0_rgba(0,0,0,0.06)]'
          : 'bg-transparent'
      }`}>
        <div className='max-w-7xl mx-auto px-6 h-16 flex items-center justify-between'>
          <Link to='/' className='flex items-center gap-2.5 group'>
            <img
              src={logo}
              alt='MediSync'
              className={`h-9 w-auto transition-all ${scrolled ? '' : 'brightness-0 invert'}`}
            />
          </Link>

          <div className='hidden md:flex items-center gap-1'>
            {NAV_LINKS.map(({ href, label }) => (
              <a
                key={href}
                href={href}
                className={`text-sm font-medium px-3 py-2 rounded-lg transition-all duration-200 ${
                  scrolled
                    ? 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                    : 'text-white/75 hover:text-white hover:bg-white/10'
                }`}
              >
                {label}
              </a>
            ))}
          </div>

          <div className='hidden md:flex items-center gap-3'>
            <Link
              to='/login'
              className={`text-sm font-medium px-4 py-2 rounded-xl transition-all ${
                scrolled
                  ? 'text-gray-700 hover:text-gray-900'
                  : 'text-white/80 hover:text-white'
              }`}
            >
              Connexion
            </Link>
            <Link
              to='/login'
              className='flex items-center gap-1.5 text-sm font-semibold bg-gradient-to-r from-navy-700 to-primary-600 text-white px-5 py-2.5 rounded-xl hover:opacity-90 transition-all shadow-lg shadow-primary-500/25 hover:shadow-primary-500/40 hover:-translate-y-px'
            >
              Démarrer gratuitement
              <ArrowRight size={14} />
            </Link>
          </div>

          <button
            onClick={() => setMenuOpen(v => !v)}
            className={`md:hidden p-2 rounded-xl transition-colors ${scrolled ? 'text-gray-700 hover:bg-gray-100' : 'text-white hover:bg-white/10'}`}
          >
            {menuOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>

        <AnimatePresence>
          {menuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className='md:hidden overflow-hidden bg-white border-t border-gray-100'
            >
              <div className='px-6 py-4 space-y-1'>
                {NAV_LINKS.map(({ href, label }) => (
                  <a
                    key={href}
                    href={href}
                    onClick={() => setMenuOpen(false)}
                    className='block text-gray-700 font-medium py-2.5 px-3 rounded-xl hover:bg-gray-50 transition-colors'
                  >
                    {label}
                  </a>
                ))}
                <Link
                  to='/login'
                  className='block text-center bg-gradient-to-r from-navy-700 to-primary-600 text-white font-semibold py-3 rounded-xl mt-3 shadow-lg shadow-primary-500/25'
                >
                  Accéder à l'application
                </Link>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>

      {/* ── Hero ───────────────────────────────────────────────────────────── */}
      <section className='relative min-h-screen flex items-center overflow-hidden bg-[#050D1F]'>
        {/* Ambient background */}
        <div className='absolute inset-0 pointer-events-none'>
          <div className='absolute top-0 left-1/4 w-[600px] h-[600px] bg-primary-600/20 rounded-full blur-[120px]' />
          <div className='absolute bottom-0 right-1/4 w-[500px] h-[500px] bg-navy-700/15 rounded-full blur-[120px]' />
          <div className='absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[400px] bg-blue-900/10 rounded-full blur-[80px]' />
          {/* Grid */}
          <div
            className='absolute inset-0 opacity-[0.04]'
            style={{
              backgroundImage: `linear-gradient(rgba(255,255,255,0.3) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.3) 1px, transparent 1px)`,
              backgroundSize: '60px 60px',
            }}
          />
          {/* Gradient fade bottom */}
          <div className='absolute bottom-0 left-0 right-0 h-40 bg-gradient-to-t from-white to-transparent' />
        </div>

        <motion.div
          style={{ y: heroY }}
          className='relative max-w-7xl mx-auto px-6 pt-28 pb-20 w-full'
        >
          <div className='text-center'>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <div className='inline-flex items-center gap-2 bg-white/5 border border-white/10 backdrop-blur-sm text-white/80 text-xs font-semibold px-4 py-2 rounded-full mb-8 tracking-wide'>
                <span className='w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse' />
                Plateforme certifiée pour professionnels de santé
              </div>
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.1 }}
              className='text-5xl md:text-7xl lg:text-8xl font-black text-white mb-6 leading-[1.05] tracking-tight'
            >
              La gestion des soins
              <br />
              <span className='text-transparent bg-clip-text bg-gradient-to-r from-primary-300 via-teal-200 to-white'>
                réinventée
              </span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className='text-lg md:text-xl text-white/50 max-w-2xl mx-auto mb-10 leading-relaxed font-light'
            >
              MediSync est la plateforme tout-en-un pour les infirmiers libéraux. Patients, tournées, messagerie et conformité réglementaire — sur web et mobile.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className='flex flex-col sm:flex-row gap-4 justify-center items-center mb-16'
            >
              <Link
                to='/login'
                className='group flex items-center gap-2.5 bg-gradient-to-r from-navy-700 to-primary-500 text-white font-bold px-8 py-4 rounded-2xl hover:opacity-90 transition-all shadow-2xl shadow-primary-500/30 hover:shadow-primary-500/50 hover:-translate-y-0.5 text-base'
              >
                Accéder à MediSync
                <ArrowRight size={18} className='group-hover:translate-x-0.5 transition-transform' />
              </Link>
              <a
                href='#fonctionnalites'
                className='flex items-center gap-2 text-white/60 font-medium px-8 py-4 rounded-2xl border border-white/10 hover:bg-white/5 hover:text-white/80 transition-all text-base backdrop-blur-sm'
              >
                Voir les fonctionnalités
              </a>
            </motion.div>

            {/* Stats */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.5 }}
              className='grid grid-cols-2 md:grid-cols-4 gap-4 max-w-3xl mx-auto'
            >
              {STATS.map(({ value, label, icon: Icon }) => (
                <div key={label} className='bg-white/5 border border-white/8 backdrop-blur-sm rounded-2xl p-5 text-center hover:bg-white/8 transition-colors'>
                  <Icon size={18} className='text-primary-400 mx-auto mb-2' />
                  <div className='text-2xl font-black text-white mb-0.5'>{value}</div>
                  <div className='text-xs text-white/40 font-medium'>{label}</div>
                </div>
              ))}
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.2 }}
            className='flex justify-center mt-16'
          >
            <motion.a
              href='#fonctionnalites'
              animate={{ y: [0, 6, 0] }}
              transition={{ repeat: Infinity, duration: 2.5, ease: 'easeInOut' }}
              className='text-white/20 hover:text-white/40 transition-colors'
            >
              <ChevronDown size={28} />
            </motion.a>
          </motion.div>
        </motion.div>
      </section>

      {/* ── Features ───────────────────────────────────────────────────────── */}
      <section id='fonctionnalites' className='py-28 bg-white'>
        <div className='max-w-7xl mx-auto px-6'>
          <FadeIn className='text-center mb-16'>
            <SectionLabel>Fonctionnalités</SectionLabel>
            <h2 className='text-4xl md:text-5xl font-black text-gray-900 mt-4 mb-5 leading-tight tracking-tight'>
              Tout ce dont vous avez besoin,
              <br />
              <span className='text-transparent bg-clip-text bg-gradient-to-r from-navy-700 to-primary-500'>en un seul endroit</span>
            </h2>
            <p className='text-gray-500 text-lg max-w-xl mx-auto leading-relaxed'>
              Conçu par et pour les infirmiers libéraux, MediSync simplifie chaque aspect de votre exercice au quotidien.
            </p>
          </FadeIn>

          <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5'>
            {FEATURES.map(({ icon: Icon, title, desc, color, gradient }, i) => (
              <FadeIn key={title} delay={i * 0.07}>
                <motion.div
                  whileHover={{ y: -6, scale: 1.01 }}
                  transition={{ type: 'spring', stiffness: 400, damping: 20 }}
                  className={`bg-gradient-to-br ${gradient} border border-gray-100 rounded-3xl p-7 h-full cursor-default hover:border-gray-200 hover:shadow-xl transition-all duration-300`}
                >
                  <div
                    className='w-12 h-12 rounded-2xl flex items-center justify-center mb-5 shadow-lg'
                    style={{ backgroundColor: `${color}18` }}
                  >
                    <Icon size={22} style={{ color }} />
                  </div>
                  <div className='flex items-center gap-2 flex-wrap mb-2.5'>
                    <h3 className='font-bold text-gray-900 text-lg tracking-tight'>{title}</h3>
                    {title === 'Optimisation des tournées' && (
                      <span className='rounded-full bg-emerald-100 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide text-emerald-700'>
                        En développement
                      </span>
                    )}
                  </div>
                  <p className='text-gray-500 text-sm leading-relaxed'>{desc}</p>
                </motion.div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* ── Comment ça marche ──────────────────────────────────────────────── */}
      <section id='comment' className='py-28 bg-gray-50'>
        <div className='max-w-7xl mx-auto px-6'>
          <FadeIn className='text-center mb-16'>
            <SectionLabel>Comment ça marche</SectionLabel>
            <h2 className='text-4xl md:text-5xl font-black text-gray-900 mt-4 mb-5 tracking-tight'>
              Opérationnel en quelques minutes
            </h2>
            <p className='text-gray-500 text-lg max-w-xl mx-auto'>
              Une mise en route simple, une prise en main intuitive.
            </p>
          </FadeIn>

          <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 relative'>
            {/* Ligne de connexion (desktop) */}
            <div className='hidden lg:block absolute top-10 left-[12.5%] right-[12.5%] h-px bg-gradient-to-r from-transparent via-blue-200 to-transparent' />

            {STEPS.map(({ step, title, desc, icon: Icon }, i) => (
              <FadeIn key={step} delay={i * 0.1}>
                <div className='flex flex-col items-center text-center px-4'>
                  <div className='relative mb-6'>
                    <div className='w-20 h-20 rounded-3xl bg-white border-2 border-blue-100 flex items-center justify-center shadow-lg shadow-blue-500/10 z-10 relative'>
                      <Icon size={28} className='text-blue-600' />
                    </div>
                    <span className='absolute -top-2 -right-2 w-7 h-7 bg-gradient-to-br from-navy-700 to-primary-600 text-white text-xs font-black rounded-full flex items-center justify-center shadow-md'>
                      {step}
                    </span>
                  </div>
                  <h3 className='font-bold text-gray-900 text-base mb-2 tracking-tight'>{title}</h3>
                  <p className='text-gray-500 text-sm leading-relaxed'>{desc}</p>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* ── Mobile ─────────────────────────────────────────────────────────── */}
      <section id='mobile' className='py-28 bg-white overflow-hidden'>
        <div className='max-w-7xl mx-auto px-6'>
          <div className='grid grid-cols-1 lg:grid-cols-2 gap-20 items-center'>
            <FadeIn>
              <SectionLabel>Application mobile</SectionLabel>
              <h2 className='text-4xl md:text-5xl font-black text-gray-900 mt-4 mb-6 leading-tight tracking-tight'>
                Votre assistant de soins,
                <br />partout avec vous
              </h2>
              <p className='text-gray-500 text-lg mb-8 leading-relaxed'>
                L'app MediSync Mobile accompagne les infirmiers sur le terrain. Synchronisée en temps réel avec la version web, elle fonctionne même sans connexion.
              </p>

              <ul className='space-y-3 mb-10'>
                {MOBILE_FEATURES.map(f => (
                  <li key={f} className='flex items-start gap-3'>
                    <div className='w-5 h-5 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0 mt-0.5'>
                      <Check size={11} className='text-blue-600' strokeWidth={3} />
                    </div>
                    <span className='text-gray-600 text-sm leading-relaxed'>{f}</span>
                  </li>
                ))}
              </ul>

              <div className='flex items-center gap-3'>
                {['App Store', 'Google Play'].map(store => (
                  <div
                    key={store}
                    className='flex items-center gap-2.5 bg-gray-900 hover:bg-gray-800 text-white px-5 py-3 rounded-xl text-sm font-semibold cursor-pointer transition-colors'
                  >
                    <Smartphone size={16} />
                    {store}
                  </div>
                ))}
              </div>
            </FadeIn>

            {/* Phone mockup */}
            <FadeIn delay={0.2} className='flex justify-center lg:justify-end'>
              <div className='relative'>
                <div className='absolute inset-0 bg-gradient-to-br from-blue-500/20 to-indigo-500/20 blur-3xl rounded-full scale-110' />
                <motion.div
                  animate={{ y: [0, -12, 0] }}
                  transition={{ repeat: Infinity, duration: 4, ease: 'easeInOut' }}
                  className='relative w-72 bg-[#0A0A0F] rounded-[3.5rem] p-3.5 shadow-2xl border border-white/10'
                >
                  {/* Notch */}
                  <div className='absolute top-3.5 left-1/2 -translate-x-1/2 w-24 h-7 bg-[#0A0A0F] rounded-full z-10' />

                  <div className='bg-gradient-to-b from-[#0F1629] to-[#0B1220] rounded-[2.8rem] overflow-hidden h-[580px] flex flex-col'>
                    {/* Status bar */}
                    <div className='flex items-center justify-between px-7 pt-6 pb-3'>
                      <span className='text-white/60 text-xs font-medium'>9:41</span>
                      <div className='flex items-center gap-1.5'>
                        <div className='flex gap-0.5'>
                          {[1,2,3,4].map(b => (
                            <div key={b} className={`w-1 rounded-full bg-white/${b * 20}`} style={{ height: b * 3 + 2 }} />
                          ))}
                        </div>
                        <div className='text-white/60 text-xs'>●●●</div>
                        <div className='w-6 h-3 rounded-sm border border-white/30 flex items-center px-0.5'>
                          <div className='w-3/4 h-1.5 bg-emerald-400 rounded-sm' />
                        </div>
                      </div>
                    </div>

                    {/* App header */}
                    <div className='px-5 pb-4'>
                      <div className='flex items-center justify-between mb-5'>
                        <div>
                          <p className='text-white/40 text-xs'>Bonjour,</p>
                          <p className='text-white font-bold text-lg'>Sophie Moreau</p>
                        </div>
                        <div className='w-10 h-10 rounded-2xl bg-blue-500/20 border border-blue-400/20 flex items-center justify-center'>
                          <Bell size={16} className='text-blue-400' />
                        </div>
                      </div>

                      {/* Today card */}
                      <div className='bg-gradient-to-br from-navy-700 to-primary-600 rounded-2xl p-4 mb-4 shadow-xl shadow-primary-900/40'>
                        <div className='flex items-center justify-between mb-3'>
                          <p className='text-blue-200 text-xs font-medium'>Aujourd'hui</p>
                          <span className='bg-white/20 text-white text-xs font-bold px-2 py-0.5 rounded-full'>6 visites</span>
                        </div>
                        <div className='w-full bg-white/20 rounded-full h-1.5 mb-1.5'>
                          <div className='bg-white h-1.5 rounded-full w-3/5' />
                        </div>
                        <p className='text-blue-200 text-xs'>3 / 6 visites effectuées</p>
                      </div>

                      {/* Patient list */}
                      <div className='space-y-2.5'>
                        {[
                          { name: 'Marie Dupont', time: '08h30', done: true, color: 'bg-emerald-400/20 text-emerald-400' },
                          { name: 'Jean Petit', time: '10h00', done: true, color: 'bg-emerald-400/20 text-emerald-400' },
                          { name: 'Claire Martin', time: '11h30', done: false, color: 'bg-blue-400/20 text-blue-400' },
                        ].map(p => (
                          <div key={p.name} className='bg-white/5 border border-white/8 rounded-xl p-3 flex items-center gap-3'>
                            <div className='w-8 h-8 rounded-xl bg-white/10 flex items-center justify-center flex-shrink-0'>
                              <Heart size={12} className='text-white/60' />
                            </div>
                            <div className='flex-1 min-w-0'>
                              <p className='text-white text-xs font-semibold truncate'>{p.name}</p>
                              <p className='text-white/40 text-xs'>{p.time}</p>
                            </div>
                            <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${p.color}`}>
                              {p.done ? 'Fait' : 'En cours'}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Tab bar */}
                    <div className='mt-auto bg-white/5 border-t border-white/8 px-4 py-3 flex justify-around'>
                      {[
                        { icon: Heart, label: 'Accueil', active: true },
                        { icon: Calendar, label: 'Planning', active: false },
                        { icon: Map, label: 'Tournée', active: false },
                        { icon: MessageSquare, label: 'Messages', active: false },
                      ].map(({ icon: Icon, label, active }) => (
                        <div key={label} className='flex flex-col items-center gap-1'>
                          <div className={`w-8 h-8 rounded-xl flex items-center justify-center ${active ? 'bg-blue-500/20' : ''}`}>
                            <Icon size={16} className={active ? 'text-blue-400' : 'text-white/30'} />
                          </div>
                          <span className={`text-[9px] font-medium ${active ? 'text-blue-400' : 'text-white/20'}`}>{label}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </motion.div>
              </div>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* ── Sécurité ───────────────────────────────────────────────────────── */}
      <section id='securite' className='py-28 bg-[#050D1F] relative overflow-hidden'>
        <div className='absolute inset-0 pointer-events-none'>
          <div className='absolute top-0 right-0 w-[500px] h-[500px] bg-blue-600/10 rounded-full blur-[120px]' />
          <div className='absolute bottom-0 left-0 w-[400px] h-[400px] bg-indigo-600/10 rounded-full blur-[100px]' />
        </div>

        <div className='relative max-w-7xl mx-auto px-6'>
          <FadeIn className='text-center mb-16'>
            <SectionLabel>Sécurité & Conformité</SectionLabel>
            <h2 className='text-4xl md:text-5xl font-black text-white mt-4 mb-5 tracking-tight'>
              La protection des données,
              <br />
              <span className='text-transparent bg-clip-text bg-gradient-to-r from-primary-300 to-teal-200'>notre priorité absolue</span>
            </h2>
            <p className='text-white/40 text-lg max-w-xl mx-auto leading-relaxed'>
              Les données de santé sont parmi les plus sensibles. MediSync est conçu dès le départ avec la sécurité comme fondation incontournable.
            </p>
          </FadeIn>

          <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 mb-14'>
            {SECURITY_ITEMS.map(({ icon: Icon, title, desc }, i) => (
              <FadeIn key={title} delay={i * 0.08}>
                <div className='bg-white/[0.03] border border-white/[0.07] rounded-2xl p-6 hover:bg-white/[0.06] hover:border-white/[0.12] transition-all duration-300 group'>
                  <div className='w-11 h-11 bg-blue-500/15 rounded-xl flex items-center justify-center mb-4 group-hover:bg-blue-500/25 transition-colors'>
                    <Icon size={20} className='text-blue-400' />
                  </div>
                  <h3 className='font-bold text-white text-sm mb-2 tracking-tight'>{title}</h3>
                  <p className='text-white/40 text-sm leading-relaxed'>{desc}</p>
                </div>
              </FadeIn>
            ))}
          </div>

          {/* Compliance badges */}
          <FadeIn className='flex flex-wrap justify-center gap-4'>
            {COMPLIANCE_BADGES.map(({ label, sublabel }) => (
              <div key={label} className='flex items-center gap-3 bg-white/[0.04] border border-white/[0.08] rounded-2xl px-6 py-4 hover:bg-white/[0.07] transition-colors'>
                <div className='w-8 h-8 bg-emerald-500/15 rounded-lg flex items-center justify-center'>
                  <ShieldCheck size={16} className='text-emerald-400' />
                </div>
                <div>
                  <p className='text-white font-bold text-sm'>{label}</p>
                  <p className='text-white/30 text-xs'>{sublabel}</p>
                </div>
              </div>
            ))}
          </FadeIn>
        </div>
      </section>

      {/* ── FAQ ────────────────────────────────────────────────────────────── */}
      <section id='faq' className='py-28 bg-gray-50'>
        <div className='max-w-3xl mx-auto px-6'>
          <FadeIn className='text-center mb-14'>
            <SectionLabel>FAQ</SectionLabel>
            <h2 className='text-4xl font-black text-gray-900 mt-4 mb-5 tracking-tight'>
              Questions fréquentes
            </h2>
            <p className='text-gray-500 text-lg'>
              Tout ce que vous devez savoir avant de commencer.
            </p>
          </FadeIn>

          <div className='space-y-3'>
            {FAQ.map(({ q, a }, i) => (
              <FadeIn key={i} delay={i * 0.05}>
                <div className='bg-white border border-gray-200 rounded-2xl overflow-hidden hover:border-gray-300 transition-colors'>
                  <button
                    onClick={() => setOpenFaq(openFaq === i ? null : i)}
                    className='w-full flex items-center justify-between p-6 text-left group'
                  >
                    <span className='font-semibold text-gray-900 text-sm pr-4 leading-relaxed'>{q}</span>
                    <motion.div
                      animate={{ rotate: openFaq === i ? 180 : 0 }}
                      transition={{ duration: 0.2 }}
                      className='flex-shrink-0'
                    >
                      <ChevronDown size={18} className='text-gray-400 group-hover:text-gray-600 transition-colors' />
                    </motion.div>
                  </button>
                  <AnimatePresence>
                    {openFaq === i && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.25 }}
                        className='overflow-hidden'
                      >
                        <p className='text-gray-500 text-sm leading-relaxed px-6 pb-6 border-t border-gray-100 pt-4'>
                          {a}
                        </p>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA Final ──────────────────────────────────────────────────────── */}
      <section className='py-28 bg-white'>
        <div className='max-w-4xl mx-auto px-6'>
          <FadeIn>
            <div className='relative bg-gradient-to-br from-[#050D1F] to-[#0F1A3A] rounded-3xl p-12 md:p-16 text-center overflow-hidden'>
              {/* Ambient */}
              <div className='absolute top-0 left-1/4 w-64 h-64 bg-blue-600/20 rounded-full blur-3xl pointer-events-none' />
              <div className='absolute bottom-0 right-1/4 w-64 h-64 bg-indigo-600/15 rounded-full blur-3xl pointer-events-none' />

              <div className='relative'>
                <div className='w-14 h-14 bg-gradient-to-br from-navy-700 to-primary-500 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-xl shadow-primary-500/30'>
                  <Heart size={24} className='text-white' fill='white' />
                </div>
                <h2 className='text-3xl md:text-5xl font-black text-white mb-5 tracking-tight leading-tight'>
                  Prêt à simplifier
                  <br />votre quotidien ?
                </h2>
                <p className='text-white/50 text-lg mb-10 max-w-xl mx-auto leading-relaxed'>
                  Rejoignez les infirmiers qui font confiance à MediSync pour gérer leurs soins, tournées et patients au quotidien.
                </p>
                <Link
                  to='/login'
                  className='inline-flex items-center gap-3 bg-gradient-to-r from-navy-700 to-primary-500 text-white font-bold text-lg px-10 py-4 rounded-2xl hover:opacity-90 transition-all shadow-2xl shadow-primary-500/30 hover:shadow-primary-500/50 hover:-translate-y-0.5'
                >
                  Accéder à MediSync
                  <ArrowRight size={20} />
                </Link>
                <p className='text-white/25 text-xs mt-5'>
                  Données hébergées en France · Certifié HDS · Conforme RGPD
                </p>
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* ── Footer ─────────────────────────────────────────────────────────── */}
      <footer className='bg-gray-950 text-gray-500 py-14'>
        <div className='max-w-7xl mx-auto px-6'>
          <div className='grid grid-cols-1 md:grid-cols-4 gap-10 mb-12'>
            <div className='col-span-1 md:col-span-2'>
              <div className='flex items-center gap-2.5 mb-4'>
                <div className='w-8 h-8 rounded-xl bg-gradient-to-br from-navy-700 to-primary-600 flex items-center justify-center'>
                  <Heart size={15} className='text-white' fill='white' />
                </div>
                <span className='font-bold text-white text-lg'>MediSync</span>
              </div>
              <p className='text-sm leading-relaxed text-gray-600 max-w-xs'>
                Plateforme de gestion des soins pour infirmiers libéraux. Conforme aux normes françaises de santé numérique.
              </p>
              <div className='flex items-center gap-2 mt-5'>
                <div className='w-2 h-2 rounded-full bg-emerald-500' />
                <span className='text-xs text-gray-600'>Tous les systèmes opérationnels</span>
              </div>
            </div>

            <div>
              <p className='text-xs font-bold uppercase tracking-widest text-gray-700 mb-4'>Produit</p>
              <ul className='space-y-3'>
                {['Fonctionnalités', 'Application mobile', 'Sécurité', 'FAQ'].map(item => (
                  <li key={item}>
                    <a href='#' className='text-sm text-gray-600 hover:text-white transition-colors'>{item}</a>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <p className='text-xs font-bold uppercase tracking-widest text-gray-700 mb-4'>Légal</p>
              <ul className='space-y-3'>
                <li><a href='/legal?tab=privacy' className='text-sm text-gray-600 hover:text-white transition-colors'>Politique de confidentialité</a></li>
                <li><a href='/legal?tab=cgu' className='text-sm text-gray-600 hover:text-white transition-colors'>CGU</a></li>
                <li><a href='/legal?tab=legal' className='text-sm text-gray-600 hover:text-white transition-colors'>Mentions légales</a></li>
                <li><a href='mailto:b.maxime6711@gmail.com' className='text-sm text-gray-600 hover:text-white transition-colors'>Contact</a></li>
              </ul>
            </div>
          </div>

          <div className='border-t border-gray-900 pt-8 flex flex-col md:flex-row items-center justify-between gap-4'>
            <p className='text-xs text-gray-700'>
              © {new Date().getFullYear()} MediSync — ALTEVO · Données hébergées en France
            </p>
            <div className='flex items-center gap-4'>
              {COMPLIANCE_BADGES.map(({ label }) => (
                <span key={label} className='text-xs text-gray-700 bg-gray-900 px-2.5 py-1 rounded-full font-medium'>
                  {label}
                </span>
              ))}
            </div>
          </div>
        </div>
      </footer>
    </div>
  )
}
